document.addEventListener('DOMContentLoaded', function () {
  // Store all swiper instances
  const swiperInstances = {
    outer: {},
    main: {},
    thumb: {}
  };
  const defaultMoneyFormat = (window.Shopify && window.Shopify.money_format) || window.moneyFormat || '${{amount}}';
  const priceOnlyFormat = '${{amount}}';

  function formatMoneyValue(value) {
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(value, defaultMoneyFormat);
    }
    const num = Number(value || 0) / 100;
    return num.toFixed(2);
  }

  function formatMoneyPriceOnly(value) {
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(value, priceOnlyFormat);
    }
    const num = Number(value || 0) / 100;
    return '$' + num.toFixed(2);
  }
  
  function buildVariantMetaFromProduct(product) {
    const meta = {};
    if (!product || !Array.isArray(product.variants)) {
      return meta;
    }
    product.variants.forEach(variant => {
      const hasCompare = variant.compare_at_price && variant.compare_at_price > (variant.price || 0);
      meta[String(variant.id)] = {
        formatted_price: formatMoneyValue(variant.price || 0),
        formatted_compare_at_price: hasCompare ? formatMoneyValue(variant.compare_at_price) : null,
        url: product.url ? `${product.url}?variant=${variant.id}` : (variant.url || '')
      };
    });
    return meta;
  }
  
  function parseJsonFromScript(scriptEl) {
    if (!scriptEl) return null;
    try {
      return JSON.parse(scriptEl.textContent);
    } catch (error) {
      console.warn('JSON parse error', error);
      return null;
    }
  }
  
  function preloadImages(imageList = []) {
    const loaders = imageList.map((img) => {
      return new Promise((resolve) => {
        const image = new Image();
        image.onload = () => resolve();
        image.onerror = () => resolve();
        image.src = img.src;
      });
    });
    return Promise.all(loaders);
  }

  // --- A11y: radio-button keyboard behavior (roving tabindex + Arrow keys) ---
  function getRadioButtons(groupEl) {
    if (!groupEl) return [];
    return Array.from(groupEl.querySelectorAll('[role="radio"].variant-button, [role="radio"].tab-button, [role="radio"].color-product-swatch'));
  }

  function isColorRadioGroup(groupEl) {
    if (!groupEl) return false;
    return (
      groupEl.matches?.('[data-color-swatches], [data-color-hidden-buttons]') ||
      !!groupEl.closest?.('[data-color-swatches], [data-color-hidden-buttons]')
    );
  }

  function isColorRadio(btn) {
    if (!btn) return false;
    if (btn.classList.contains('color-product-swatch')) return true;
    if (btn.hasAttribute('data-color-product-id')) return true;
    if (btn.closest('[data-color-swatches], [data-color-hidden-buttons]')) return true;
    return false;
  }

  function isDisabledRadio(btn) {
    if (!btn) return true;
    if (btn.classList.contains('disabled')) return true;
    const ariaDisabled = (btn.getAttribute('aria-disabled') || '').toLowerCase();
    if (ariaDisabled === 'true') return true;
    if (btn.hasAttribute('disabled')) return true;
    return false;
  }

  function isCheckedRadio(btn) {
    if (!btn) return false;
    if (btn.classList.contains('active')) return true;
    if (btn.classList.contains('g-active')) return true;
    if (btn.classList.contains('is-active')) return true;
    const ariaChecked = (btn.getAttribute('aria-checked') || '').toLowerCase();
    return ariaChecked === 'true';
  }

  function setCheckedRadio(groupEl, btnToCheck) {
    // We intentionally do NOT apply roving tabindex for Color groups (only Gender/Size/Width).
    if (isColorRadioGroup(groupEl)) return;
    const radios = getRadioButtons(groupEl);
    radios.forEach((btn) => {
      const checked = btn === btnToCheck;
      btn.setAttribute('aria-checked', checked ? 'true' : 'false');
      // Roving tabindex: only the checked radio is tabbable. If none checked, first enabled becomes tabbable.
      btn.tabIndex = checked ? 0 : -1;
    });
  }

  function syncRadioGroupA11y(groupEl) {
    const radios = getRadioButtons(groupEl);
    if (!radios.length) return;

    // Skip Color groups entirely (requested behavior).
    if (isColorRadioGroup(groupEl)) return;

    // Ensure aria-disabled reflects current disabled state
    radios.forEach((btn) => {
      btn.setAttribute('aria-disabled', isDisabledRadio(btn) ? 'true' : 'false');
    });

    const checked = radios.find((btn) => isCheckedRadio(btn) && !isDisabledRadio(btn));
    const fallback = radios.find((btn) => !isDisabledRadio(btn)) || radios[0];
    setCheckedRadio(groupEl, checked || fallback);
  }

  function findRadioGroupFromButton(btn) {
    if (!btn) return null;
    return (
      btn.closest('[role="radiogroup"]') ||
      btn.closest('[data-variant-buttons]') ||
      btn.closest('[data-color-swatches]') ||
      btn.closest('.tab-navigation-row') ||
      null
    );
  }

  function moveFocusInRadioGroup(groupEl, currentBtn, direction /* -1 | 1 */, wrap = true) {
    const radios = getRadioButtons(groupEl).filter((b) => !isDisabledRadio(b));
    if (!radios.length) return null;
    const currentIndex = Math.max(0, radios.indexOf(currentBtn));
    let nextIndex = currentIndex + direction;
    if (wrap) {
      if (nextIndex < 0) nextIndex = radios.length - 1;
      if (nextIndex >= radios.length) nextIndex = 0;
    } else {
      nextIndex = Math.min(Math.max(nextIndex, 0), radios.length - 1);
    }
    return radios[nextIndex] || null;
  }

  // Initialize tab functionality
  function initTabs() {
    const tabButtons = document.querySelectorAll('.tab-button');
    const tabContents = document.querySelectorAll('.tab-content-wrapper');
    
    // Activate first tab by default if no tab is active
    const activeTab = document.querySelector('.tab-content-wrapper.active');
    if (!activeTab && tabContents.length > 0) {
      const firstTab = tabContents[0];
      const firstTabId = firstTab.getAttribute('data-tab-id');
      firstTab.classList.add('active');
      // Activate corresponding button
      tabButtons.forEach(btn => {
        if (btn.getAttribute('data-tab-id') === firstTabId) {
          btn.classList.add('g-active');
        }
      });
    }
    
    tabButtons.forEach(button => {
      button.addEventListener('click', function() {
        const tabId = this.getAttribute('data-tab-id');
        
        // Remove active class from all buttons and contents
        tabButtons.forEach(btn => btn.classList.remove('g-active'));
        tabContents.forEach(content => {
          content.classList.remove('active');
        });
        
        // Add active class to clicked button and corresponding content
        tabButtons.forEach(btn => btn.getAttribute('data-tab-id') === tabId && btn.classList.add('g-active'));
          
        const targetContent = document.querySelector(`.tab-content-wrapper[data-tab-id="${tabId}"]`);
        if (targetContent) {
          targetContent.classList.add('active');
          // Swiper was inited when tab was hidden (0 dimensions) - update on show
          setTimeout(() => {
            const outerSwiper = swiperInstances.outer[tabId];
            if (outerSwiper && typeof outerSwiper.update === 'function') {
              outerSwiper.update();
            }
            targetContent.querySelectorAll('[data-product-container]').forEach(container => {
              const pid = container.getAttribute('data-product-container');
              [swiperInstances.main[pid], swiperInstances.thumb[pid]].forEach(swiper => {
                if (swiper && typeof swiper.update === 'function') swiper.update();
              });
            });
          }, 50);
        }
      });
    });

    // A11y: arrow-key navigation for the gender/tab radio group(s)
    document.querySelectorAll('.tab-navigation-row[role="radiogroup"]').forEach((groupEl) => {
      syncRadioGroupA11y(groupEl);
      if (groupEl.dataset.radioKeysBound === 'true') return;
      groupEl.dataset.radioKeysBound = 'true';

      groupEl.addEventListener('keydown', (e) => {
        const btn = e.target.closest('[role="radio"]');
        if (!btn || !groupEl.contains(btn)) return;

        const key = e.key;
        const isArrow =
          key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowUp' || key === 'ArrowDown';
        const isHomeEnd = key === 'Home' || key === 'End';
        const isActivate = key === ' ' || key === 'Enter';
        if (!isArrow && !isHomeEnd && !isActivate) return;

        e.preventDefault();
        e.stopPropagation();

        if (isActivate) {
          if (!isDisabledRadio(btn)) btn.click();
          return;
        }

        const enabledRadios = getRadioButtons(groupEl).filter((b) => !isDisabledRadio(b));
        if (!enabledRadios.length) return;

        let target = null;
        if (isHomeEnd) {
          target = key === 'Home' ? enabledRadios[0] : enabledRadios[enabledRadios.length - 1];
        } else {
          const dir = key === 'ArrowLeft' || key === 'ArrowUp' ? -1 : 1;
          target = moveFocusInRadioGroup(groupEl, btn, dir, true);
        }

        if (!target) return;
        target.focus();
        // For radios, arrows also change selection.
        target.click();
      });
    });
  }

  // Initialize outer slider (for multiple products in a tab)
  function initOuterSliders() {
    const outerSwipers = document.querySelectorAll('.outer-product-swiper');
    
    outerSwipers.forEach(swiperEl => {
      const tabId = swiperEl.getAttribute('data-tab-id');
      
      // Destroy existing instance if it exists
      if (swiperInstances.outer[tabId]) {
        swiperInstances.outer[tabId].destroy(true, true);
      }
      
      const tabContent = swiperEl.closest('.tab-content-wrapper');
      const isFirstTab = tabContent && tabContent === document.querySelector('.pdp-wrapper-section .tab-content-wrapper');

      swiperInstances.outer[tabId] = new Swiper(swiperEl, {
        slidesPerView: 1,
        spaceBetween: 0,
        effect: 'fade',
        fadeEffect: {
          crossFade: true
        },
        // Disable swipe/touch scroll on the outer slider; navigation arrows still work
        allowTouchMove: false,
        navigation: {
          nextEl: `.outer-next-${tabId}`,
          prevEl: `.outer-prev-${tabId}`
        },
        on: isFirstTab ? {
          slideChange: function() {
            if (this.realIndex !== 0) return;
            const firstSlide = swiperEl.querySelector('.swiper-slide');
            const container = firstSlide?.querySelector('[data-product-container]');
            if (!container?._variantController || !container?._linkedColorMap) return;
            const productId = container.getAttribute('data-product-container');
            const mainWrapper = container.querySelector(`.mySwiper-${productId} .swiper-wrapper`);
            const hasImages = mainWrapper && mainWrapper.querySelectorAll('.swiper-slide img').length > 0;
            if (hasImages) return;
            const activeId = container.dataset.activeProductId;
            const product = container._linkedColorMap[activeId];
            if (product && typeof container._variantController.setProduct === 'function') {
              container._variantController.setProduct(product, { forceImageUpdate: true });
            }
          }
        } : {}
      });
    });
  }

  // Initialize all product displays (variant selection and image sliders)
  function initAllProductDisplays() {
    // Find all product containers across all tabs - init once at load, tabs just show/hide
    const productContainers = document.querySelectorAll('.tab-content-wrapper [data-product-container]');
    
    productContainers.forEach(container => {
      const productId = container.getAttribute('data-product-container');
      // Initialize ALL product containers (all outer slides) so thumbnails, add-to-cart,
      // and variant selection work for every slide when user switches
      initProductDisplay(container, productId);
    });
  }

  // Initialize a single product display
  function initProductDisplay(container, productId) {
    // Get product JSON data
    const productJsonEl = container.querySelector(`[data-product-id="${productId}"].product-json`);
    const variantMetaEl = container.querySelector(`[data-product-id="${productId}"].variant-meta`);
    const linkedColorsEl = container.querySelector(`[data-product-id="${productId}"].linked-color-products`);
    
    if (!productJsonEl) return;
    
    const product = parseJsonFromScript(productJsonEl);
    const variantMeta = parseJsonFromScript(variantMetaEl) || buildVariantMetaFromProduct(product);
    const linkedColorProducts = parseJsonFromScript(linkedColorsEl) || [];
    
    if (!product) return;
    
    // Initialize image sliders
    initImageSliders(container, productId, product);
    
    // Initialize variant selection
    const variantController = initVariantSelection(container, productId, product, variantMeta);
    
    initLinkedColorProducts(container, productId, variantController, linkedColorProducts, product);
  }

    // Initialize image sliders (main and thumbnail)
  function initImageSliders(container, productId, product) {
    const mainSwiperEl = container.querySelector(`.mySwiper-${productId}`);
    const thumbSwiperEl = container.querySelector(`.mySwiper2-${productId}`);
    
    if (!mainSwiperEl || !thumbSwiperEl || !document.body.contains(mainSwiperEl) || !document.body.contains(thumbSwiperEl)) return;
    
    // Destroy existing instances - main first (it references thumb), then thumb
    if (swiperInstances.main[productId]) {
      try { swiperInstances.main[productId].destroy(true, true); } catch (e) { /* ignore */ }
      swiperInstances.main[productId] = null;
    }
    if (swiperInstances.thumb[productId]) {
      try { swiperInstances.thumb[productId].destroy(true, true); } catch (e) { /* ignore */ }
      swiperInstances.thumb[productId] = null;
    }
    
    try {
      // Initialize thumbnail swiper first
      swiperInstances.thumb[productId] = new Swiper(thumbSwiperEl, {
        spaceBetween: 10,
        slidesPerView: 3,
        freeMode: true,
        watchSlidesProgress: true,
        mousewheel: {
          enabled: true,
          forceToAxis: true,
          sensitivity: 1,
          releaseOnEdges: false,
        }
      });
      
      const thumbInstance = swiperInstances.thumb[productId];
      if (!thumbInstance || !thumbInstance.el) return;
      
      // Determine navigation elements (guard for missing buttons)
      const prevButton = container.querySelector(`.main-prev-${productId}`);
      const nextButton = container.querySelector(`.main-next-${productId}`);
      const navigationConfig = {};
      if (prevButton) navigationConfig.prevEl = prevButton;
      if (nextButton) navigationConfig.nextEl = nextButton;
      
      // Initialize main swiper with thumbs
      swiperInstances.main[productId] = new Swiper(mainSwiperEl, {
        spaceBetween: 10,
        effect: 'fade',
        fadeEffect: {
          crossFade: true
        },
        ...(Object.keys(navigationConfig).length ? { navigation: navigationConfig } : {}),
        mousewheel: {
          enabled: true,
          forceToAxis: true,
          sensitivity: 1,
          releaseOnEdges: false,
        },
        thumbs: {
          swiper: thumbInstance
        }
      });

      // Manual thumb click handler - thumbs module can be unreliable with freeMode/fade
      // Use delegation so it works after DOM rebuild; bind once per element
      if (!thumbSwiperEl.dataset.thumbClickBound) {
        thumbSwiperEl.dataset.thumbClickBound = 'true';
        thumbSwiperEl.addEventListener('click', function thumbClickHandler(e) {
          const slide = e.target.closest('.swiper-slide');
          if (!slide || !thumbSwiperEl.contains(slide)) return;
          const slides = thumbSwiperEl.querySelectorAll('.swiper-slide');
          const index = Array.from(slides).indexOf(slide);
          if (index === -1) return;
          const mainSwiper = swiperInstances.main[productId];
          if (mainSwiper && typeof mainSwiper.slideTo === 'function') {
            mainSwiper.slideTo(index);
          }
        });
      }
    } catch (e) {
      // Swiper init can fail if DOM is transitional (e.g. during color switch fade)
      if (swiperInstances.thumb[productId]) {
        try { swiperInstances.thumb[productId].destroy(true, true); } catch (_) {}
        swiperInstances.thumb[productId] = null;
      }
      swiperInstances.main[productId] = null;
    }
  }

  // Initialize variant selection for a product
  function initVariantSelection(container, productId, initialProduct, initialVariantMeta) {
    const variantContainer = container.querySelector(`[data-variant-container="${productId}"]`);
    if (!variantContainer) return;
    
   const groups = variantContainer.querySelectorAll('.variant-option-group');
   
   // Function to get ALL variant-id-input elements within this product container
   // Specifically targets inputs in mid-slider-area (sibling to variant-selectors)
   // Only gets inputs for the ACTIVE product (matching data-active-product-id)
   const getVariantIdInputs = () => {
     const selectedProductRow = variantContainer.closest('.selected-product-row');
     if (!selectedProductRow) {
       console.warn('[VariantSelection] No selected-product-row found');
       return [];
     }
     const containerId = container.getAttribute('data-product-container');
     
     // Find mid-slider-area (sibling to variant-selectors)
     const midSliderArea = selectedProductRow.querySelector('.mid-slider-area');
     
     // Get all variant-id-input elements from mid-slider-area and mobile form
     const inputs = [];
     if (midSliderArea) {
       const midSliderInputs = midSliderArea.querySelectorAll('.variant-id-input');
       inputs.push(...Array.from(midSliderInputs));
     }
     
     // Also get mobile form inputs (in product-key.mobile-discription)
     const mobileFormInputs = selectedProductRow.querySelectorAll('.product-key.mobile-discription .variant-id-input');
     inputs.push(...Array.from(mobileFormInputs));
     
     // Filter to only include inputs that belong to this product container
     const filteredInputs = Array.from(inputs).filter(input => {
       const inputContainer = input.closest('[data-product-container]');
       const inputContainerId = inputContainer?.getAttribute('data-product-container');
       return !inputContainerId || inputContainerId === containerId;
     });
     
     return filteredInputs;
   };
   
   // Function to get ALL add to cart buttons within this product container
   const getAddToCartButtons = () => {
     const buttons = container.querySelectorAll('.btn.btn-primary');
     
     // Filter to only include buttons that belong to this product container
     const filteredButtons = Array.from(buttons).filter(button => {
       const buttonContainer = button.closest('[data-product-container]');
       const buttonContainerId = buttonContainer?.getAttribute('data-product-container');
       const containerId = container.getAttribute('data-product-container');
       return !buttonContainerId || buttonContainerId === containerId;
     });
     
     return filteredButtons;
   };
    const galleryEl = container.querySelector('.product-gallery');
    const mainSwiperSelector = `.mySwiper-${productId}`;
    let galleryHeightLocked = false;
    
    const getGalleryHeight = () => {
      if (!galleryEl) return 0;
      let height = galleryEl.getBoundingClientRect().height;
      if (height > 200) return height; // Only use if reasonable height
      const mainSwiperEl = galleryEl.querySelector(mainSwiperSelector);
      if (mainSwiperEl) {
        const swiperRect = mainSwiperEl.getBoundingClientRect();
        if (swiperRect.height > 200) return swiperRect.height;
        height = mainSwiperEl.scrollHeight;
        if (height > 200) return height;
      }
      const activeImage = galleryEl.querySelector(`${mainSwiperSelector} .swiper-slide-active img`);
      if (activeImage) {
        // Wait for image to load if not already loaded
        if (activeImage.complete && activeImage.naturalWidth > 0 && activeImage.naturalHeight > 0) {
          const width = activeImage.clientWidth || activeImage.naturalWidth || 0;
          const naturalWidth = activeImage.naturalWidth || 0;
          const naturalHeight = activeImage.naturalHeight || 0;
          if (naturalWidth > 0 && naturalHeight > 0) {
            const ratio = width > 0 ? width / naturalWidth : 1;
            const calculatedHeight = naturalHeight * ratio;
            if (calculatedHeight > 200) return calculatedHeight;
          }
        }
      }
      const scrollHeight = galleryEl.scrollHeight;
      return scrollHeight > 200 ? scrollHeight : 0;
    };
    
    const lockGalleryHeight = (skipIfSmall = true) => {
      if (!galleryEl || galleryHeightLocked) return false;
      const measuredHeight = getGalleryHeight();
      // Only lock if we have a reasonable height (prevent locking at 101px)
      if (measuredHeight > 200 || !skipIfSmall) {
        galleryEl.style.minHeight = `${measuredHeight}px`;
        galleryHeightLocked = true;
        return true;
      }
      return false;
    };
    
    const unlockGalleryHeight = () => {
      if (!galleryEl || !galleryHeightLocked) return;
      galleryEl.style.minHeight = '';
      galleryHeightLocked = false;
    };
    
    const colorOptionIndex = 0; // Color/Gender is the first option
    const sizeOptionIndex = 1; // Size is the second option (0=Color, 1=Size, 2=Width)
    const widthOptionIndex = 2; // Width is the third option (0=Color, 1=Size, 2=Width)
    const variantButtonsWrappers = variantContainer.querySelectorAll('[data-variant-buttons]');
    const colorHiddenButtons = variantContainer.querySelectorAll('[data-color-hidden-buttons] .variant-button');
    let activeProduct = initialProduct;
    let lastImageUpdateKey = ''; // 'productId|color' - skip image rebuild if unchanged
    let imageSliderUpdateTimeout = null;
    let activeVariantMeta = initialVariantMeta;
    let linkedMapRef = null; // Set by initLinkedColorProducts for fallback when DOM color doesn't match activeProduct
    
    function rebuildVariantButtonsForProduct(productToUse) {
      if (!productToUse || !productToUse.options_with_values) return;
      
      variantButtonsWrappers.forEach(wrapper => {
        const group = wrapper.closest('.variant-option-group');
        if (!group) return;
        const optionIndex = Number(group.getAttribute('data-option-index'));
        if (optionIndex === colorOptionIndex) return;
        const optionName = group.getAttribute('data-option-name');
        const shouldBeRadio = optionIndex === sizeOptionIndex || optionIndex === widthOptionIndex;
        const optionData = productToUse.options_with_values.find(opt => {
          if (opt.position) {
            return Number(opt.position) - 1 === optionIndex;
          }
          return opt.name === optionName;
        });
        if (!optionData) {
          wrapper.innerHTML = '';
          return;
        }
        const buttonsHtml = optionData.values
          .map(value => {
            const safeLabel = String(value).replace(/"/g, '&quot;');
            if (!shouldBeRadio) {
              return `<button type="button" class="variant-button" data-value="${safeLabel}">${safeLabel}</button>`;
            }
            // Size/Width: preserve radio semantics so Arrow-key behavior works and tab stops are correct
            return `<button type="button" class="variant-button" data-value="${safeLabel}" role="radio" aria-checked="false" aria-disabled="false" aria-label="${safeLabel}" tabindex="-1">${safeLabel}</button>`;
          })
          .join('');
        wrapper.innerHTML = buttonsHtml;
      });
    }
    
    // Clear Size only; never clear Width (Width is always auto-selected to first option)
    function clearNonColorSelections() {
      groups.forEach(group => {
        const optionIndex = group.getAttribute('data-option-index');
        if (optionIndex === '0') return; // skip Color
        if (optionIndex === '2') return; // skip Width - preserve/auto-select first width
        group.querySelectorAll('.variant-button').forEach(btn => btn.classList.remove('active'));
      });
    }
    
    rebuildVariantButtonsForProduct(activeProduct);
    
    // Select first Size option in the DOM and return its value (for overrides).
    function selectFirstSizeOption() {
      const sizeGroup = Array.from(groups).find(g => g.getAttribute('data-option-index') === '1');
      if (!sizeGroup) return null;
      const sizeOpt = activeProduct?.options_with_values?.[sizeOptionIndex];
      const firstSizeValue = sizeOpt?.values?.[0];
      if (!firstSizeValue) return null;
      const buttons = sizeGroup.querySelectorAll('.variant-button');
      const toSelect = Array.from(buttons).find(btn => btn.getAttribute('data-value') === firstSizeValue && !btn.classList.contains('disabled'))
        || Array.from(buttons).find(btn => btn.getAttribute('data-value') === firstSizeValue);
      if (toSelect) {
        buttons.forEach(btn => btn.classList.remove('active'));
        toSelect.classList.add('active');
      }
      return firstSizeValue;
    }

    // Select first Width option in the DOM and return its value (for overrides).
    function selectFirstWidthOption() {
      const widthGroup = Array.from(groups).find(g => g.getAttribute('data-option-index') === '2');
      if (!widthGroup) return null;
      const widthOpt = activeProduct?.options_with_values?.[widthOptionIndex];
      const firstWidthValue = widthOpt?.values?.[0];
      if (!firstWidthValue) return null;
      const buttons = widthGroup.querySelectorAll('.variant-button');
      const toSelect = Array.from(buttons).find(btn => btn.getAttribute('data-value') === firstWidthValue && !btn.classList.contains('disabled'))
        || Array.from(buttons).find(btn => btn.getAttribute('data-value') === firstWidthValue);
      if (toSelect) {
        buttons.forEach(btn => btn.classList.remove('active'));
        toSelect.classList.add('active');
      }
      return firstWidthValue;
    }
    
    function setActiveProductContext(newProduct, contextOptions = {}) {
      if (!newProduct) return;
      lastImageUpdateKey = ''; // Reset so new product's images always update
      
      // Clear variant-id-input when product changes (tab change or color variant change)
      const inputs = getVariantIdInputs();
      inputs.forEach(input => {
        input.setAttribute('value', '');
        input.value = '';
      });
      
      activeProduct = newProduct;
      activeVariantMeta = contextOptions.variantMeta || buildVariantMetaFromProduct(newProduct);
      rebuildVariantButtonsForProduct(activeProduct);
      
      // On color/gender change: always select default (first) Size and Width
      // This prevents width options from being disabled when carrying over an incompatible size
      clearNonColorSelections();
      const firstSizeValue = selectFirstSizeOption();
      const firstWidthValue = selectFirstWidthOption();
      const overrides = {
        ...(firstSizeValue != null ? { [sizeOptionIndex]: firstSizeValue } : {}),
        ...(firstWidthValue != null ? { [widthOptionIndex]: firstWidthValue } : {}),
        ...(contextOptions.selectionOverrides || {})
      };
      
      // Update UI - this will fill variant-id-input based on selected variant options
      return updateUIForVariant({
        selectionOverrides: overrides,
        sourceProduct: contextOptions.sourceProduct || newProduct,
        forceImageUpdate: contextOptions.forceImageUpdate,
        isInitialLoad: contextOptions.isInitialLoad || false
      });
    }
    // Get selected options from active buttons
    function getSelectedOptions(overrides = {}) {
      const options = Array.from(groups).map((g, index) => {
        if (Object.prototype.hasOwnProperty.call(overrides, index)) {
          return overrides[index];
        }
        
        // For color option (index 0), check both active button and active swatch
        if (index === colorOptionIndex) {
          // First check for active button in hidden buttons
          const hiddenButtons = g.querySelector('[data-color-hidden-buttons]');
          if (hiddenButtons) {
            const activeHiddenBtn = hiddenButtons.querySelector('.variant-button.active');
            if (activeHiddenBtn) {
              const value = activeHiddenBtn.getAttribute('data-value');
              if (value) return value;
            }
          }
          
          // Check for active swatch
          const activeSwatch = g.querySelector('.color-product-swatch.is-active, .color-product-swatch.active');
          if (activeSwatch) {
            const colorLabel = activeSwatch.getAttribute('data-color-label');
            if (colorLabel) return colorLabel;
          }
          
          // If no active button/swatch, use the active product's color_label as default
          if (activeProduct && activeProduct.color_label) {
            return activeProduct.color_label;
          }
          
          // Fallback: get first option value from active product
          if (activeProduct && activeProduct.options_with_values && activeProduct.options_with_values[0]) {
            const firstOption = activeProduct.options_with_values[0];
            if (firstOption.values && firstOption.values.length > 0) {
              return firstOption.values[0];
            }
          }
        }
        
        // For other options (Size, Width), check active button
        const activeBtn = g.querySelector('.variant-button.active');
        const value = activeBtn ? activeBtn.getAttribute('data-value') : null;
        return value;
      });
      
      return options;
    }
    
    // Find variant by selected options (exact match)
    // productToSearch: optional - use this product's variants instead of activeProduct (for linked color fallback)
    function findVariantByOptions(selectedOptions, productToSearch) {
      const product = productToSearch || activeProduct;
      if (!product || !product.variants) return null;
      
      const found = product.variants.find(v => {
        if (Array.isArray(v.options)) {
          return v.options.every((opt, i) => opt === selectedOptions[i]);
        }
        if (selectedOptions.length === 1 && v.option1) {
          return v.option1 === selectedOptions[0];
        }
        if (selectedOptions.length === 2 && v.option1 && v.option2) {
          return v.option1 === selectedOptions[0] && v.option2 === selectedOptions[1];
        }
        if (selectedOptions.length === 3 && v.option1 && v.option2 && v.option3) {
          return v.option1 === selectedOptions[0] && v.option2 === selectedOptions[1] && v.option3 === selectedOptions[2];
        }
        return false;
      });
      
      // No variant found - fallback in updateUIForVariant will try linked product
      
      return found || null;
    }
    
    // Check if any variant exists with the given partial options (null values are ignored)
    function hasVariantWithOptions(testOptions) {
      if (!activeProduct || !activeProduct.variants) return false;
      return activeProduct.variants.some(v => {
        // Check each option position - if testOptions[i] is not null, it must match
        for (let i = 0; i < testOptions.length; i++) {
          if (testOptions[i] !== null && testOptions[i] !== undefined) {
            let variantOption = null;
            if (Array.isArray(v.options)) {
              variantOption = v.options[i];
            } else {
              if (i === 0) variantOption = v.option1;
              else if (i === 1) variantOption = v.option2;
              else if (i === 2) variantOption = v.option3;
            }
            if (variantOption !== testOptions[i]) {
              return false;
            }
          }
        }
        return true;
      });
    }
    
    // Check if all required options are selected (excluding Gender/Color - data-option-index="0")
    function areAllOptionsSelected() {
      const groupsArray = Array.from(groups);
      // Filter out groups with data-option-index="0" (Gender/Color)
      const groupsToCheck = groupsArray.filter(group => {
        const optionIndex = group.getAttribute('data-option-index');
        return optionIndex !== '0';
      });
      
      // If there are no groups to check (only Gender exists), return true
      if (groupsToCheck.length === 0) return true;
      
      // Check if all groups (except data-option-index="0") have an active button
      const allSelected = groupsToCheck.every(group => {
        const buttons = group.querySelectorAll('.variant-button');
        if (buttons.length === 0) {
          return true; // If no buttons in group, consider it valid
        }
        const hasActive = Array.from(buttons).some(btn => btn.classList.contains('active'));
        return hasActive;
      });
      
      return allSelected;
    }
    
    // Variant buttons: do not disable unavailable combinations (per requirement)
    function updateVariantButtonStates() {
      groups.forEach((group) => {
        group.querySelectorAll('.variant-button').forEach((btn) => {
          btn.classList.remove('disabled');
          btn.removeAttribute('disabled');
          btn.setAttribute('aria-disabled', 'false');
        });
      });
    }
    
    // Update UI based on selected variant
    function updateUIForVariant(options = {}) {
      const selectionOverrides = options.selectionOverrides || {};
      const selectedOptions = getSelectedOptions(selectionOverrides);
      let variant = findVariantByOptions(selectedOptions);
      
      // Fallback: if no variant found but selected color from DOM doesn't match activeProduct
      // (e.g. color swatch was clicked but activeProduct not yet updated), try the linked product
      if (!variant && linkedMapRef && selectedOptions[colorOptionIndex]) {
        const activeSwatch = variantContainer.querySelector('.color-product-swatch.is-active, .color-product-swatch.active');
        const swatchProductId = activeSwatch?.getAttribute('data-color-product-id');
        if (swatchProductId) {
          const resolvedProduct = linkedMapRef[swatchProductId];
          if (resolvedProduct && resolvedProduct.id !== activeProduct?.id) {
            variant = findVariantByOptions(selectedOptions, resolvedProduct);
            if (variant) {
              activeProduct = resolvedProduct;
              activeVariantMeta = buildVariantMetaFromProduct(resolvedProduct);
              rebuildVariantButtonsForProduct(resolvedProduct);
            }
          }
        }
      }
      
      // Update disabled states of variant buttons
      updateVariantButtonStates();
      
      // Check if all options are selected (excluding color/gender)
      const allSelected = areAllOptionsSelected();
      
      if (variant && allSelected) {
        const vm = activeVariantMeta[String(variant.id)] || {};
        
        // Re-query inputs and buttons each time to ensure we get the latest elements
        const variantIdInputs = getVariantIdInputs();
        const addToCartButtons = getAddToCartButtons();
        
         // Update variant ID in ALL forms for the current active product
         if (variantIdInputs.length === 0) {
           console.warn('[VariantSelection] No variant-id-input elements found in container!', {
             container: container,
             containerSelector: `[data-product-container="${productId}"]`,
             allInputsInPage: document.querySelectorAll('.variant-id-input').length
           });
         } else {
           // Update ALL variant-id-input elements within this container
           // But only for the active product
           const selectedProductRow = variantContainer.closest('.selected-product-row');
           const activeProductId = selectedProductRow?.getAttribute('data-active-product-id');
           
           variantIdInputs.forEach((input, idx) => {
             // Double-check: verify this input belongs to the active product
             const inputSelectedRow = input.closest('.selected-product-row');
             const inputActiveProductId = inputSelectedRow?.getAttribute('data-active-product-id');
             
             // Skip if this input doesn't belong to the active product
             if (activeProductId && inputActiveProductId && inputActiveProductId !== activeProductId) {
               return;
             }
             
             const oldValue = input.value;
             const variantIdString = String(variant.id);
             
             // Set the value using multiple methods to ensure it sticks
             input.setAttribute('value', variantIdString);
             input.value = variantIdString;
             
             // Force a change event to ensure any listeners are notified
             input.dispatchEvent(new Event('change', { bubbles: true }));
             input.dispatchEvent(new Event('input', { bubbles: true }));
             
             // Verify the value was actually set
             if (input.value !== variantIdString) {
               // Retry with direct property assignment
               Object.defineProperty(input, 'value', {
                 value: variantIdString,
                 writable: true,
                 configurable: true
               });
               input.setAttribute('value', variantIdString);
             }
           });
         }
        
        // Update price block (compare at price + price) in money format $ only (no US)
        const priceBlocks = container.querySelectorAll('[data-variant-price-block]');
        const displayPrice = formatMoneyPriceOnly(variant.price || 0);
        const displayCompare = (variant.compare_at_price && variant.compare_at_price > (variant.price || 0)) ? formatMoneyPriceOnly(variant.compare_at_price) : null;
        priceBlocks.forEach(block => {
          const compareEl = block.querySelector('.variant-price-block__compare');
          const priceEl = block.querySelector('.variant-price-block__price');
          if (compareEl) {
            if (displayCompare) {
              compareEl.textContent = displayCompare;
              compareEl.style.display = '';
            } else {
              compareEl.style.display = 'none';
            }
          }
          if (priceEl) priceEl.textContent = displayPrice;
        });

        // Update add to cart button: text only "Add to Cart" or "Sold Out"
        addToCartButtons.forEach((button) => {
          if (variant.available) {
            button.textContent = 'Add to Cart';
            button.disabled = false;
            button.style.opacity = '1';
            button.style.cursor = 'pointer';
          } else {
            button.textContent = 'Sold Out';
            button.disabled = true;
          }
        });
         } else {
           // Re-query inputs and buttons each time
           const variantIdInputs = getVariantIdInputs();
           const addToCartButtons = getAddToCartButtons();
           
           // No variant found or not all options selected
           if (allSelected) {
             // All required options are selected but no variant found - show Unavailable
             // All options selected but no variant found - can happen briefly during color switch
             
             // Clear ALL variant-id-input elements
             variantIdInputs.forEach((input) => {
               input.setAttribute('value', '');
               input.value = '';
               input.dispatchEvent(new Event('change', { bubbles: true }));
               input.dispatchEvent(new Event('input', { bubbles: true }));
             });
             
             // Show default variant price (money format $ only) when no variant found
             const defaultVariantUnavailable = activeProduct?.variants?.[0];
             const defaultPriceUnavailable = defaultVariantUnavailable ? formatMoneyPriceOnly(defaultVariantUnavailable.price || 0) : '';
             const defaultCompareUnavailable = defaultVariantUnavailable && defaultVariantUnavailable.compare_at_price && defaultVariantUnavailable.compare_at_price > (defaultVariantUnavailable.price || 0) ? formatMoneyPriceOnly(defaultVariantUnavailable.compare_at_price) : null;
             container.querySelectorAll('[data-variant-price-block]').forEach(block => {
               const compareEl = block.querySelector('.variant-price-block__compare');
               const priceEl = block.querySelector('.variant-price-block__price');
               if (compareEl) {
                 if (defaultCompareUnavailable) {
                   compareEl.textContent = defaultCompareUnavailable;
                   compareEl.style.display = '';
                 } else {
                   compareEl.style.display = 'none';
                 }
               }
               if (priceEl) priceEl.textContent = defaultPriceUnavailable;
             });
             addToCartButtons.forEach(button => {
               button.textContent = 'Unavailable';
               button.disabled = true;
               button.style.opacity = '0.6';
               button.style.cursor = 'not-allowed';
             });
           } else {
             // Not all options selected - show default variant price until selection is made
             variantIdInputs.forEach((input) => {
               input.setAttribute('value', '');
               input.value = '';
               input.dispatchEvent(new Event('change', { bubbles: true }));
               input.dispatchEvent(new Event('input', { bubbles: true }));
             });
             const defaultVariant = activeProduct?.variants?.[0];
             const defaultPrice = defaultVariant ? formatMoneyPriceOnly(defaultVariant.price || 0) : '';
             const defaultCompare = defaultVariant && defaultVariant.compare_at_price && defaultVariant.compare_at_price > (defaultVariant.price || 0) ? formatMoneyPriceOnly(defaultVariant.compare_at_price) : null;
             container.querySelectorAll('[data-variant-price-block]').forEach(block => {
               const compareEl = block.querySelector('.variant-price-block__compare');
               const priceEl = block.querySelector('.variant-price-block__price');
               if (compareEl) {
                 if (defaultCompare) {
                   compareEl.textContent = defaultCompare;
                   compareEl.style.display = '';
                 } else {
                   compareEl.style.display = 'none';
                 }
               }
               if (priceEl) priceEl.textContent = defaultPrice;
             });
             addToCartButtons.forEach(button => {
               button.textContent = 'Add to Cart';
               button.disabled = false;
               button.style.opacity = '1';
               button.style.cursor = 'pointer';
             });
           }
         }
      
      // Only update images when color/product changed or forceImageUpdate - skip on size/width change to prevent jerking
      const color = selectedOptions[colorOptionIndex];
      const sourceProduct = options.sourceProduct || activeProduct;
      const updateKey = (sourceProduct?.id || '') + '|' + (color || '');
      if (options.forceImageUpdate || options.isInitialLoad || updateKey !== lastImageUpdateKey) {
        lastImageUpdateKey = updateKey;
        return updateImageSlider(color, sourceProduct, options.forceImageUpdate, options.isInitialLoad || false);
      }
      return Promise.resolve();
    }
    
    // Read all images from product
    function readImagesFromProduct(sourceProduct = activeProduct) {
      const images = [];
      if (sourceProduct && Array.isArray(sourceProduct.media)) {
        sourceProduct.media.forEach(m => {
          const src = m.preview_image?.src || m.src || '';
          const alt = m.alt || '';
          if (src) images.push({ src, alt, media: m });
        });
      } else if (sourceProduct && Array.isArray(sourceProduct.images)) {
        sourceProduct.images.forEach(img => {
          if (typeof img === 'string') images.push({ src: img, alt: '', media: null });
          else images.push({ src: img.src || '', alt: img.alt || '', media: img });
        });
      }
      return images;
    }
    
    // Update image slider based on color selection (debounced to prevent jerking from rapid calls)
    function updateImageSlider(colorValue, sourceProduct = activeProduct, forceAll = false, isInitialLoad = false) {
      if (imageSliderUpdateTimeout) clearTimeout(imageSliderUpdateTimeout);
      const doUpdate = () => {
        imageSliderUpdateTimeout = null;
        if (isInitialLoad) {
          unlockGalleryHeight();
        } else {
          lockGalleryHeight();
        }
        const images = readImagesFromProduct(sourceProduct);
        const color = colorValue?.toLowerCase().trim() || '';
      const rebuild = (imageSet) => {
        return rebuildImageSlider(container, productId, imageSet, sourceProduct);
      };
      const prepareImages = (imageSet) => preloadImages(imageSet).catch(() => {});
      
      // Wait for images to load in DOM before calculating height
      const waitForImagesLoad = () => {
        return new Promise((resolve) => {
          const mainSwiperEl = galleryEl?.querySelector(mainSwiperSelector);
          if (!mainSwiperEl) {
            resolve();
            return;
          }
          const images = mainSwiperEl.querySelectorAll('img');
          if (images.length === 0) {
            resolve();
            return;
          }
          let loadedCount = 0;
          const checkComplete = () => {
            loadedCount++;
            if (loadedCount >= images.length) {
              // Wait for layout to settle
              requestAnimationFrame(() => {
                requestAnimationFrame(resolve);
              });
            }
          };
          images.forEach(img => {
            if (img.complete && img.naturalHeight > 0) {
              checkComplete();
            } else {
              img.addEventListener('load', checkComplete, { once: true });
              img.addEventListener('error', checkComplete, { once: true });
            }
          });
          // Timeout fallback
          setTimeout(resolve, 2000);
        });
      };
      
      const processImages = (imageSet) => {
        return prepareImages(imageSet)
          .then(() => rebuild(imageSet))
          .then(() => {
            if (!isInitialLoad) {
              // Wait for images to load, then unlock and re-lock with correct height
              return waitForImagesLoad().then(() => {
                unlockGalleryHeight();
                lockGalleryHeight();
              });
            } else {
              // On initial load, just wait for images to ensure proper rendering
              return waitForImagesLoad();
            }
          });
      };
      
      if (forceAll) {
        return processImages(images);
      }
      
      if (!color) {
        // No color selected, show only common images (no prefix)
        const commonImages = images.filter(img => {
          const alt = img.alt?.toLowerCase().trim() || '';
          return !alt.startsWith('home_') && !alt.startsWith('pdp_');
        });
        const finalImages = commonImages.length > 0 ? commonImages : images;
        return processImages(finalImages);
      }
      
      // Filter images for home page:
      // Include: home_colorname and colorname (common)
      // Exclude: pdp_colorname
      const matched = images.filter(img => {
        const alt = img.alt?.toLowerCase().trim() || '';
        
        // Check if it's a home-specific image (home_colorname)
        if (alt === `home_${color}`) {
          return true;
        }
        
        // Check if it's a common image (colorname without prefix)
        if (alt === color) {
          return true;
        }
        
        // Exclude pdp images
        return false;
      });
      
      // If no images match, show only common images (no home_ or pdp_ prefix)
      if (matched.length === 0) {
        const commonImages = images.filter(img => {
          const alt = img.alt?.toLowerCase().trim() || '';
          return !alt.startsWith('home_') && !alt.startsWith('pdp_');
        });
        const finalImages = commonImages.length > 0 ? commonImages : images;
        return processImages(finalImages);
      } else {
        return processImages(matched);
      }
      };
      // On color switch (forceImageUpdate) or initial load: run immediately, return real promise
      // so fade-in waits for new images. Debounce only for size/width changes.
      if (isInitialLoad || forceAll) {
        return doUpdate();
      }
      imageSliderUpdateTimeout = setTimeout(() => doUpdate(), 80);
      return Promise.resolve();
    }
    
    // Build HTML for slides
    function buildSlidesHtml(images) {
      return images.map(img => {
        return `<div class="swiper-slide"><img src="${img.src}" alt="${img.alt}" class="main-swiper-image" width="600" height="auto" loading="lazy"></div>`;
      }).join('');
    }
    
    function buildThumbSlidesHtml(images) {
      return images.map(img => {
        return `<div class="swiper-slide"><img src="${img.src}" alt="${img.alt}" width="150" height="auto" loading="lazy"></div>`;
      }).join('');
    }
    
    // Rebuild image slider with new images
    function rebuildImageSlider(container, productId, images, sourceProduct = activeProduct) {
      const mainWrapper = container.querySelector(`.mySwiper-${productId} .swiper-wrapper`);
      const thumbWrapper = container.querySelector(`.mySwiper2-${productId} .swiper-wrapper`);
      
      if (!mainWrapper || !thumbWrapper) return;
      
      // Update HTML with filtered images
      mainWrapper.innerHTML = buildSlidesHtml(images);
      thumbWrapper.innerHTML = buildThumbSlidesHtml(images);
      
      // Defer swiper reinit so DOM is settled (avoids Swiper "el" null error during color switch)
      return new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            initImageSliders(container, productId, sourceProduct);
            requestAnimationFrame(resolve);
          });
        });
      });
    }
    
    // Show popup message for missing selections (only once per product per validation attempt)
    function showSelectionPopup(productContainer) {
      // Use data attribute to track if popup was shown for this product
      const popupShown = productContainer.getAttribute('data-popup-shown');
      if (popupShown === 'true') {
        return;
      }
      
      productContainer.setAttribute('data-popup-shown', 'true');
      
      // Show modal instead of alert
      if (window.SelectionRequiredModal && typeof window.SelectionRequiredModal.open === 'function') {
        window.SelectionRequiredModal.open();
        
        // Reset flag after modal is closed
        const modal = document.querySelector('.pahi-selection-modal');
        if (modal) {
          const handleClose = () => {
            productContainer.removeAttribute('data-popup-shown');
            modal.removeEventListener('transitionend', handleClose);
          };
          // Wait for modal close animation
          setTimeout(() => {
            productContainer.removeAttribute('data-popup-shown');
          }, 500);
        }
      } else {
        // Fallback to alert if modal is not available
        alert('Please select Size before adding to cart.');
        setTimeout(() => {
          productContainer.removeAttribute('data-popup-shown');
        }, 100);
      }
    }
    
    // Bind form submissions to validate selections and handle AJAX cart add
    // Skip forms that already have listener (prevents duplicate requests when re-initing on slide change)
    const addToCartForms = container.querySelectorAll('.add-to-cart-form');
    addToCartForms.forEach(form => {
      if (form.dataset.addToCartBound === 'true') return;
      form.dataset.addToCartBound = 'true';

      form.addEventListener('submit', (e) => {
        e.preventDefault(); // Always prevent default to use AJAX

        // Prevent multiple concurrent requests (safety guard)
        if (form.dataset.addToCartSubmitting === 'true') {
          e.stopImmediatePropagation();
          return false;
        }

        // Find the product container that contains this form
        const productContainer = form.closest('[data-product-container]');
        if (!productContainer) {
          return;
        }
        
        // Check if this container is in the active tab
        const activeTab = document.querySelector('.tab-content-wrapper.active');
        if (!activeTab || !activeTab.contains(productContainer)) {
          return; // Not in active tab, skip validation
        }
        
        // Check if this container is in the active slider slide (if it's in a slider)
        const parentSlide = productContainer.closest('.swiper-slide');
        if (parentSlide && !parentSlide.classList.contains('swiper-slide-active')) {
          const variantInput = form.querySelector('.variant-id-input');
          console.log('[AddToCart] Form from inactive slide - variant-id-input:', variantInput?.value ?? variantInput?.getAttribute('value') ?? '(empty)', { productContainerId: productContainer?.getAttribute('data-product-container') });
          return; // Not in active slide, skip validation
        }
        
        // Debug: log variant IDs on every Add to Cart click
        const formVariantInput = form.querySelector('.variant-id-input');
        const formVariantValue = formVariantInput?.value || formVariantInput?.getAttribute('value') || '';
        let formData = new FormData(form);
        const formDataId = (formData.get('id') || '').toString().trim();
        const activeSlide = activeTab?.querySelector('.outer-product-swiper .swiper-slide-active') ||
          document.querySelector('.outer-product-swiper .swiper-slide-active');
        const activeContainer = activeSlide?.querySelector('[data-product-container]');
        const activeFormInputs = activeContainer?.querySelectorAll('.variant-id-input') || [];
        const activeVariantIds = Array.from(activeFormInputs).map(inp => inp.value || inp.getAttribute('value') || '');
        // When user slides without selecting on first product, the submitted form may be from
        // the wrong slide. Use ACTIVE slide's variant-id-input as source of truth.
        let variantIdFromActiveSlide = '';
        for (const inp of activeFormInputs) {
          const v = (inp.value || inp.getAttribute('value') || '').toString().trim();
          if (v) { variantIdFromActiveSlide = v; break; }
        }
        let variantIdFromForm = formDataId;
        if (!variantIdFromForm && variantIdFromActiveSlide && activeContainer?.contains(form) === false) {
          // Submitted form is from a different (inactive) slide; use active slide's form for add to cart
          const activeFormWithId = Array.from(activeFormInputs).find(inp => (inp.value || inp.getAttribute('value') || '').toString().trim())?.closest('form');
          if (activeFormWithId) {
            formData = new FormData(activeFormWithId);
            variantIdFromForm = variantIdFromActiveSlide;
          }
        } else if (!variantIdFromForm && variantIdFromActiveSlide) {
          // Same container but form's FormData is empty - sync from active slide's input
          const inputToFix = form.querySelector('.variant-id-input');
          if (inputToFix) {
            inputToFix.value = variantIdFromActiveSlide;
            inputToFix.setAttribute('value', variantIdFromActiveSlide);
            formData = new FormData(form);
            variantIdFromForm = variantIdFromActiveSlide;
          }
        }
        
        if (variantIdFromForm) {
          // Variant ID is set - proceed to add to cart, skip all validation
        } else {
          // No variant ID in form - run validation and show popup if needed
          const productVariantContainer = productContainer.querySelector(`[data-variant-container="${productId}"]`);
          if (!productVariantContainer) {
            console.warn('[FormValidation] No variant container found');
            return;
          }
          const currentGroups = productVariantContainer.querySelectorAll('.variant-option-group');
          const groupsToCheck = Array.from(currentGroups).filter(group => {
            const optionIndex = group.getAttribute('data-option-index');
            return optionIndex !== '0';
          });
          const allSelected = groupsToCheck.length === 0 || groupsToCheck.every(group => {
            const buttons = group.querySelectorAll('.variant-button');
            if (buttons.length === 0) return true;
            return Array.from(buttons).some(btn => btn.classList.contains('active'));
          });
          if (!allSelected) {
            const variantInput = form.querySelector('.variant-id-input');
            console.log('[FormValidation] variant-id-input value before size popup:', variantInput?.value ?? variantInput?.getAttribute('value') ?? '(empty)');
            e.stopPropagation();
            e.stopImmediatePropagation();
            showSelectionPopup(productContainer);
            return false;
          }
        }
        
        // Get submit button and add loading state
        const submitButton = form.querySelector('button[type="submit"]');
        if (submitButton) {
          submitButton.classList.add('loading');
          submitButton.disabled = true;
        }
        form.dataset.addToCartSubmitting = 'true';
        
        // Get cart drawer
        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer) {
          console.warn('[AddToCart] Cart drawer not found, redirecting to cart page');
          form.submit(); // Fallback to normal form submission
          return;
        }
        
        // Reuse formData built above; ensure variant ID is present
        const variantId = formData.get('id');
        if (!variantId || variantId.trim() === '') {
          console.error('[AddToCart] No variant ID found in form');
          alert('Please select all required options before adding to cart.');
          form.dataset.addToCartSubmitting = '';
          if (submitButton) {
            submitButton.classList.remove('loading');
            submitButton.disabled = false;
          }
          return;
        }
        
        // Add sections to render (for cart drawer update)
        const sectionsToRender = cartDrawer.getSectionsToRender();
        if (sectionsToRender && sectionsToRender.length > 0) {
          formData.append(
            'sections',
            sectionsToRender.map((section) => section.id).join(',')
          );
          formData.append('sections_url', window.location.pathname);
        }
        
        // Set active element for focus management
        cartDrawer.setActiveElement(document.activeElement);
        
        // Configure fetch request (matching product-form.js exactly)
        const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' }
        };
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type']; // FormData sets this automatically with boundary
        config.body = formData;
        
        console.log('[AddToCart] Selected variant ID:', variantId);
        
        // Get cart add URL - use routes object if available, otherwise window.routes
        let cartAddUrl = '/cart/add';
        if (typeof routes !== 'undefined' && routes.cart_add_url) {
          cartAddUrl = routes.cart_add_url;
        } else if (window.routes && window.routes.cart_add_url) {
          cartAddUrl = window.routes.cart_add_url;
        }
        
        
        // Make AJAX request to add to cart
        fetch(cartAddUrl, config)
          .then((response) => {
            // Check if response is OK
            if (!response.ok) {
              return response.text().then(text => {
                console.error('[AddToCart] HTTP error response:', text);
                throw new Error(`HTTP error! status: ${response.status}`);
              });
            }
            
            // Check content type to determine if it's JSON or HTML
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              return response.json();
            } else {
              // If not JSON, try to parse as text first
              return response.text().then(text => {
                console.warn('[AddToCart] Response is not JSON, received:', text.substring(0, 200));
                // Try to parse as JSON anyway (might be JSON with wrong content-type)
                try {
                  return JSON.parse(text);
                } catch (e) {
                  // If parsing fails, it's likely HTML
                  throw new Error('Server returned HTML instead of JSON. This usually means the request was redirected or there was an error.');
                }
              });
            }
          })
          .then((response) => {
            // Check if response has status property (Shopify error format)
            if (response.status) {
              // Error response from Shopify
              console.error('[AddToCart] Error adding to cart:', response);
              
              // Publish cart error event if available
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-display-tab',
                  productVariantId: formData.get('id'),
                  errors: response.errors || response.description,
                  message: response.message,
                });
              }
              
              if (window.SelectionRequiredModal && typeof window.SelectionRequiredModal.open === 'function') {
                window.SelectionRequiredModal.open();
              } else {
                alert(response.description || 'Unable to add item to cart. Please try again.');
              }
              return;
            }
            
            // Success - update cart drawer
            if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
              cartDrawer.renderContents(response);
              
              // Remove is-empty class if cart drawer has it
              if (cartDrawer.classList.contains('is-empty')) {
                cartDrawer.classList.remove('is-empty');
              }
              
              // Open the cart drawer
              if (typeof cartDrawer.open === 'function') {
                cartDrawer.open();
              }
            }
            
            // Publish cart update event if available
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-display-tab',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            }
          })
          .catch((error) => {
            console.error('[AddToCart] Error:', error);
            
            // Publish cart error event if available
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-display-tab',
                productVariantId: formData.get('id'),
                errors: error.message,
                message: 'Unable to add item to cart. Please try again.',
              });
            }
            
            alert('Unable to add item to cart. Please try again.');
          })
          .finally(() => {
            form.dataset.addToCartSubmitting = '';
            if (submitButton) {
              submitButton.classList.remove('loading');
              submitButton.disabled = false;
            }
          });
      }, true); // Use capture phase to handle early
    });
    
    variantContainer.addEventListener('click', (event) => {
      const targetButton = event.target.closest('.variant-button');
      if (!targetButton || !variantContainer.contains(targetButton)) {
        return;
      }
      const group = targetButton.closest('.variant-option-group');
      if (!group) return;
      
      selectVariantButton(targetButton, group);
    });

    function selectVariantButton(btn, groupEl) {
      if (!btn || !groupEl) return;

      const buttonsInGroup = groupEl.querySelectorAll('.variant-button');
      buttonsInGroup.forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');

      // Keep ARIA + roving tabindex in sync for this group
      const groupForA11y = findRadioGroupFromButton(btn) || groupEl;
      syncRadioGroupA11y(groupForA11y);

      // Update UI immediately when size/width is selected
      updateUIForVariant();
    }

    // A11y: Arrow-key navigation for variant radio groups (Size/Width/Color swatches)
    if (variantContainer && variantContainer.dataset.radioKeysBound !== 'true') {
      variantContainer.dataset.radioKeysBound = 'true';

      // Initial sync for any existing groups in this container
      variantContainer.querySelectorAll('[role="radiogroup"], [data-variant-buttons], [data-color-swatches], .tab-navigation-row').forEach(syncRadioGroupA11y);

      variantContainer.addEventListener('keydown', (e) => {
        const btn = e.target.closest('[role="radio"]');
        if (!btn || !variantContainer.contains(btn)) return;

        // Skip Color controls: arrow-key behavior only for Size/Width (and Gender handled in initTabs()).
        if (isColorRadio(btn)) return;

        const groupEl = findRadioGroupFromButton(btn);
        if (!groupEl) return;
        if (isColorRadioGroup(groupEl)) return;

        const key = e.key;
        const isArrow =
          key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowUp' || key === 'ArrowDown';
        const isHomeEnd = key === 'Home' || key === 'End';
        const isActivate = key === ' ' || key === 'Enter';
        if (!isArrow && !isHomeEnd && !isActivate) return;

        e.preventDefault();
        e.stopPropagation();

        if (isActivate) {
          if (!isDisabledRadio(btn)) btn.click();
          return;
        }

        const enabledRadios = getRadioButtons(groupEl).filter((b) => !isDisabledRadio(b));
        if (!enabledRadios.length) return;

        let target = null;
        if (isHomeEnd) {
          target = key === 'Home' ? enabledRadios[0] : enabledRadios[enabledRadios.length - 1];
        } else {
          const dir = key === 'ArrowLeft' || key === 'ArrowUp' ? -1 : 1;
          target = moveFocusInRadioGroup(groupEl, btn, dir, true);
        }

        if (!target) return;
        target.focus();
        const targetGroup = target.closest('.variant-option-group');
        // Don't rely on click handlers (DOM can be rebuilt); run selection logic directly.
        if (targetGroup) selectVariantButton(target, targetGroup);
      });
    }
    
    // Initialize on load - preselect first Size and Width, then filter images and update variant
    const initialUpdate = () => {
      // Always preselect first Size and Width on initial load (tab/slide/gender change)
      selectFirstSizeOption();
      selectFirstWidthOption();
      
      updateUIForVariant({
        isInitialLoad: true
      });

      // Sync radio-group a11y state after initial selection + disabled computation
      variantContainer.querySelectorAll('[role="radiogroup"], [data-variant-buttons], [data-color-swatches], .tab-navigation-row').forEach(syncRadioGroupA11y);
      
      // Double-check after a short delay to ensure variant-id-input is set
      setTimeout(() => {
        const inputs = getVariantIdInputs();
        const selectedOptions = getSelectedOptions();
        const variant = findVariantByOptions(selectedOptions);
        const allSelected = areAllOptionsSelected();
        
        // If we have a variant and all options are selected, but inputs are empty, force update
        if (variant && allSelected && inputs.length > 0) {
          const hasEmptyInputs = Array.from(inputs).some(inp => !inp.value || inp.value.trim() === '');
          if (hasEmptyInputs) {
            console.warn('[VariantSelection] Found empty inputs after initial update, forcing update');
            updateUIForVariant();
          }
        }
      }, 200);
    };
    
    setTimeout(initialUpdate, 100);
    
    return {
      setProduct(newProduct, options = {}) {
        const overrides = options.selectionOverrides || {
          [colorOptionIndex]: options.colorOverride || (newProduct && newProduct.color_label) || null
        };
        return setActiveProductContext(newProduct, {
          variantMeta: options.variantMeta,
          selectionOverrides: overrides,
          sourceProduct: options.sourceProduct || newProduct,
          forceImageUpdate: options.forceImageUpdate,
          isInitialLoad: options.isInitialLoad || false
        });
      },
      getActiveProduct() {
        return activeProduct;
      },
      updateVariantId() {
        updateUIForVariant();
      },
      setLinkedMap(map) {
        linkedMapRef = map || null;
      }
    };
  }
  
  function initLinkedColorProducts(container, productId, variantController, linkedProducts, initialProduct) {
    if (!variantController || !Array.isArray(linkedProducts) || linkedProducts.length === 0) {
      return;
    }
    
    const swatchWrapper = container.querySelector('[data-color-swatches]');
    if (!swatchWrapper) return;
    const hiddenButtonsWrapper = container.querySelector('[data-color-hidden-buttons]');
    const linkedMap = {};

    // Gallery controls (fade in after gallery is ready)
    const galleryControls = container.querySelector('.gallery-controls');

    // Elements to animate on product switch
    const infoCol = container.querySelector('.product-information-col');
    const gallery = container.querySelector('.product-gallery');
    const desktopKey = container.querySelector('.product-key.desktop-discription');
    const mobileKey = container.querySelector('.product-key.mobile-discription');
    const fadeTargets = [infoCol, gallery, desktopKey, mobileKey].filter(Boolean);
    const fadeDuration = 500;
    
    linkedProducts.forEach(productData => {
      if (productData && productData.id) {
        linkedMap[String(productData.id)] = productData;
      }
    });
    if (variantController.setLinkedMap) variantController.setLinkedMap(linkedMap);
    
    let activeId = container.dataset.activeProductId;
    if (!activeId || !linkedMap[activeId]) {
      activeId = initialProduct && initialProduct.id
        ? String(initialProduct.id)
        : (linkedProducts[0] ? String(linkedProducts[0].id) : null);
    }
    if (activeId) {
      if (!linkedMap[activeId] && initialProduct) {
        linkedMap[activeId] = initialProduct;
      }
      container.dataset.activeProductId = activeId;
    }
    
    const updateHiddenButtons = (targetId, colorLabel) => {
      if (!hiddenButtonsWrapper) return;
      hiddenButtonsWrapper.querySelectorAll('.variant-button').forEach(btn => {
        if (btn.getAttribute('data-color-product-id') === targetId) {
          btn.classList.add('active');
          if (colorLabel) {
            btn.setAttribute('data-value', colorLabel);
          }
        } else {
          btn.classList.remove('active');
        }
      });
    };
    
    const updateSwatchState = (targetId) => {
      swatchWrapper.querySelectorAll('.color-product-swatch').forEach(btn => {
        const isMatch = btn.getAttribute('data-color-product-id') === targetId;
        btn.classList.toggle('is-active', isMatch);
        btn.classList.toggle('active', isMatch);
      });
    };
    
    const truncateText = (text, maxLength = 150) => {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return `${text.slice(0, maxLength - 1).trim()}…`;
    };
    
    const updateProductContent = (productData) => {
      if (!productData) return;
      const titleEl = container.querySelector(`#product-title-${productId}`);
      if (titleEl) titleEl.textContent = productData.title || '';
      const descEl = container.querySelector(`#product-description-${productId}`);
      if (descEl) {
          const rawDescription = productData.description_short || productData.description || '';
          descEl.textContent = '';
          descEl.textContent = truncateText(rawDescription);
      }
           const colorLabelEl = container.querySelector('.home-page-collection-section__color-label');
      if (colorLabelEl) colorLabelEl.textContent = productData.color_label || '';
      const featureEls = container.querySelectorAll('.product-fetures-description');
      featureEls.forEach(el => {
        el.innerHTML = productData.key_benefits || '';
      });
    };
    
    const switchToProduct = (productData, isInitialLoad = false) => {
      if (!productData) return;
      const targetId = String(productData.id);
      container.dataset.activeProductId = targetId;
      updateSwatchState(targetId);
      updateHiddenButtons(targetId, productData.color_label);
      
    const performUpdate = () => {
      updateProductContent(productData);
      const result = variantController.setProduct(productData, {
        variantMeta: buildVariantMetaFromProduct(productData),
        colorOverride: productData.color_label,
        forceImageUpdate: true,
        sourceProduct: productData,
        isInitialLoad: isInitialLoad
      });
      
      // After product switch, ensure variant-id-input is updated if size/width are already selected
      // Debounced to avoid redundant updateUIForVariant (which caused image jerking)
      const triggerUpdate = () => {
        if (variantController && typeof variantController.updateVariantId === 'function') {
          variantController.updateVariantId();
        }
      };
      
      if (result && typeof result.then === 'function') {
        result.then(() => setTimeout(triggerUpdate, 300));
      } else {
        setTimeout(triggerUpdate, 300);
      }
      
      return result;
    };

      // For initial load (tab/slider change): wait for images before fade-in to prevent jerking
      if (isInitialLoad || !fadeTargets.length) {
        const result = performUpdate();

        const waitForImagesToLoad = () => {
          return new Promise((resolve) => {
            const mainSwiperEl = gallery?.querySelector(`.mySwiper-${productId}`);
            if (!mainSwiperEl) {
              resolve();
              return;
            }
            const imgs = mainSwiperEl.querySelectorAll('img');
            if (imgs.length === 0) {
              resolve();
              return;
            }
            let loadedCount = 0;
            const checkComplete = () => {
              loadedCount++;
              if (loadedCount >= imgs.length) {
                requestAnimationFrame(() => requestAnimationFrame(resolve));
              }
            };
            imgs.forEach(img => {
              if (img.complete && img.naturalHeight > 0) {
                checkComplete();
              } else {
                img.addEventListener('load', checkComplete, { once: true });
                img.addEventListener('error', checkComplete, { once: true });
              }
            });
            setTimeout(resolve, 3000);
          });
        };

        const afterInit = async () => {
          await waitForImagesToLoad();
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              fadeTargets.forEach(el => el.classList.add('fade-content-visible'));
              if (galleryControls) galleryControls.classList.add('gallery-controls--visible');
            });
          });
        };

        if (result && typeof result.then === 'function') {
          result.then(afterInit);
        } else {
          afterInit();
        }
        return;
      }

      // Fade out gallery controls immediately (0ms)
      if (galleryControls) {
        galleryControls.classList.remove('gallery-controls--visible');
      }

      // Remove visible class to hide content (opacity: 0)
      fadeTargets.forEach(el => {
        el.classList.remove('fade-content-visible');
      });

      // Update content immediately
      const result = performUpdate();

      // Wait for images to load before fading in
      const waitForImagesToLoad = () => {
        return new Promise((resolve) => {
          const mainSwiperEl = gallery?.querySelector(`.mySwiper-${productId}`);
          if (!mainSwiperEl) {
            resolve();
            return;
          }
          const images = mainSwiperEl.querySelectorAll('img');
          if (images.length === 0) {
            resolve();
            return;
          }
          let loadedCount = 0;
          const checkComplete = () => {
            loadedCount++;
            if (loadedCount >= images.length) {
              // Wait for layout to settle
              requestAnimationFrame(() => {
                requestAnimationFrame(resolve);
              });
            }
          };
          images.forEach(img => {
            if (img.complete && img.naturalHeight > 0) {
              checkComplete();
            } else {
              img.addEventListener('load', checkComplete, { once: true });
              img.addEventListener('error', checkComplete, { once: true });
            }
          });
          // Timeout fallback after 3 seconds
          setTimeout(resolve, 3000);
        });
      };

      // Once product + gallery updated, wait for images, then fade content in
      const afterUpdate = async () => {
        // Wait for images to load
        await waitForImagesToLoad();
        
        // Small delay to ensure everything is rendered
      // await new Promise(resolve => setTimeout(resolve, 0));
        
        // Trigger smooth fade-in animation by adding visible class
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            fadeTargets.forEach(el => {
              el.classList.add('fade-content-visible');
            });
          });
        });

        // Show gallery controls at the same time as other content
        if (galleryControls) {
          galleryControls.classList.add('gallery-controls--visible');
        }
      };

      if (result && typeof result.then === 'function') {
        result.then(afterUpdate);
      } else {
        afterUpdate();
      }
    };
    
    const initialProductState = (activeId && linkedMap[activeId]) || initialProduct || linkedProducts[0];
    if (initialProductState) {
      switchToProduct(initialProductState, true); // Pass true for initial load
    }
    
    swatchWrapper.addEventListener('click', (event) => {
      const swatch = event.target.closest('.color-product-swatch');
      if (!swatch) return;
      const targetId = swatch.getAttribute('data-color-product-id');
      if (!targetId || container.dataset.activeProductId === targetId) return;
      const nextProduct = linkedMap[targetId];
      if (!nextProduct) return;
      event.stopPropagation(); // Prevent variant container handler from running - avoids race where activeProduct isn't updated yet
      switchToProduct(nextProduct);
    });

    // Store refs so first slide can be refreshed when it becomes active
    container._variantController = variantController;
    container._linkedColorMap = linkedMap;
  }

  // Section-level thumb click delegation: always use the active outer slide's main swiper
  // Fixes first slide thumbnails not responding (stacking/init order issues)
  document.addEventListener('click', (e) => {
    const thumbSlide = e.target.closest('.product-gallery .mySwiper2 .swiper-slide');
    if (!thumbSlide) return;
    const thumbSwiperEl = thumbSlide.closest('.mySwiper2');
    if (!thumbSwiperEl) return;
    const container = thumbSwiperEl.closest('[data-product-container]');
    if (!container) return;
    const activeTab = document.querySelector('.tab-content-wrapper.active');
    if (!activeTab || !activeTab.contains(container)) return;
    const activeOuterSlide = activeTab.querySelector('.outer-product-swiper .swiper-slide-active');
    if (!activeOuterSlide || !activeOuterSlide.contains(container)) return;
    const productId = container.getAttribute('data-product-container');
    if (!productId) return;
    const slides = thumbSwiperEl.querySelectorAll('.swiper-slide');
    const index = Array.from(slides).indexOf(thumbSlide);
    if (index === -1) return;
    const mainSwiper = swiperInstances.main[productId];
    if (mainSwiper && typeof mainSwiper.slideTo === 'function') {
      e.preventDefault();
      e.stopPropagation();
      mainSwiper.slideTo(index);
    }
  }, true);

  // Initialize everything
  initTabs();
  
  // Wait a bit to ensure first tab is activated before initializing products
  setTimeout(() => {
    initOuterSliders();
    initAllProductDisplays();
  }, 100);
});

